import { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import {
  toggleProductToFavourite,
  fetchFavouriteProducts,
} from '../services/product-api';
import { trackAddToFavorite } from '../lib/eventTracker';
import { useSelector } from 'react-redux';
import { getUserIsAuthenticated } from '../features/auth/authSlice';

export const useAddToFavorites = (product) => {
  const queryClient = useQueryClient();
  const isAuthenticated = useSelector(getUserIsAuthenticated);

  // Fetch all favourites (guests have none; the endpoint would just 401)
  const { data: favourites = [] } = useQuery({
    queryKey: ['favourites'],
    queryFn: fetchFavouriteProducts,
    enabled: isAuthenticated,
  });

  const [isFavourite, setIsFavourite] = useState(false);

  // Check if current product is in favourites
  useEffect(() => {
    if (product?.productID) {
      setIsFavourite(favourites.some((f) => f.productID === product.productID));
    }
  }, [favourites, product]);

  // Mutation for toggling favourite
  const mutation = useMutation({
    mutationFn: () => toggleProductToFavourite(product.productID),

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['favourites'] });

      const previousFavourites = queryClient.getQueryData(['favourites']) || [];

      const currentlyFav = previousFavourites.some(
        (f) => f.productID === product.productID
      );

      const newFavourites = currentlyFav
        ? previousFavourites.filter((f) => f.productID !== product.productID)
        : [
            ...previousFavourites,
            { productID: product.productID, productDetails: product },
          ];

      queryClient.setQueryData(['favourites'], newFavourites);
      setIsFavourite(!currentlyFav);

      return { previousFavourites, currentlyFav };
    },

    onError: (_err, _variables, context) => {
      if (context?.previousFavourites) {
        queryClient.setQueryData(['favourites'], context.previousFavourites);
      }
      if (typeof context?.currentlyFav === 'boolean') {
        setIsFavourite(context.currentlyFav);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['favourites'] });
    },
  });

  const toggleFavourite = () => {
    if (!product?.productID || mutation.isLoading) return;
    trackAddToFavorite(product);
    mutation.mutate();
  };

  return { isFavourite, toggleFavourite, isLoading: mutation.isLoading };
};
